import { Component, OnInit } from '@angular/core';
import { columnDefs, MonthOption } from './timesheet-config';
import { paginationTimesheetParams } from '@shared/config/table.config';
import { SearchService } from '@services/search.service';
import {
  TimeSheetData,
  TimesheetUpdateResponse,
} from '@interfaces/vendor-management-model';
import { ErrorMessages } from '@constants/constants';
import { VendorService } from '@services/vendor.service';
import {
  PersonaType,
  Priority,
  RoutesEnum,
  TimesheetStatus,
} from '@constants/global.enum';
import { ToasterService } from '@app/shared/services/toaster.service';
import { DataService } from '@services/data.service';
import { TIMESHEET_CONSTANTS } from './timesheet.constants';
import { sortByStatusPriority } from '@shared/helpers/timesheet-sort.helper';
import {
  getDataLoadedStatus,
  shouldShowTimesheetCheckbox,
  isEmptyTimesheet,
  getStatusIconClass,
  getHoursSuffix,
  ComponentType,
} from '@shared/helpers/timesheet.helper';
import { ExportService } from '@services/export.service';
import {columnsForExport} from './timesheet-config';

@Component({
  selector: 'app-timesheet',
  templateUrl: './timesheet.component.html',
  styleUrl: './timesheet.component.scss',
})
export class TimesheetComponent implements OnInit {
  isTimesheetCalendarOpen = false;
  selectedRowData: any = null;
  isCalendarOpen = false;
  calendarPosition: { top: string; left: string } = {
    top: TIMESHEET_CONSTANTS.POSITION_STYLES.INITIAL_TOP,
    left: TIMESHEET_CONSTANTS.POSITION_STYLES.INITIAL_LEFT
  };
  columnDefs = columnDefs;
  hoverTip = { show: false, text: '', x: 0, y: 0 };
  month: number = 0;
  year: number = 0;
  paginationParams = paginationTimesheetParams;
  data: TimeSheetData[] = [];
  displayData: TimeSheetData[] = [];
  originalData: TimeSheetData[] = [];
  statusFilteredData: TimeSheetData[] = [];
  isStatusFilterApplied: boolean = false;
  filterCriteria: {project?: string, reportingManager?: string, status?: string, dntLeader?: string, employeeStatus?: string} = {};
  filterValue: string = '';
  isDataLoaded: boolean = true;
  priority = Priority;
  opened: boolean = false;
  imageSrc: string = TIMESHEET_CONSTANTS.IMAGES.NO_DATA_FOUND;
  errorMsg: string = ErrorMessages.NoDataAvailable;
  selectedOption: string = '';
  monthOptions: MonthOption[] = [];
  selectedRows: TimeSheetData[] = [];
  componentName: string = PersonaType.vendorRepTimesheet;
  personaType = PersonaType;
  TimesheetStatus = TimesheetStatus;
  TIMESHEET_CONSTANTS = TIMESHEET_CONSTANTS;
  showStatusChangeDialog: boolean = false;
  statusChangeDialogData: { employeeId: string; employeeName: string }[] = [];
  routesEnum = RoutesEnum;
  showContractorProfile = false;
  contractorId: string | null = null;
  showSubmitConfirmation: boolean = false;
  showExcludeConfirmation: boolean = false;
  pendingExcludeRow: TimeSheetData | null = null;

  isRowSelectableFunction = (row: TimeSheetData) => {
    return this.shouldShowCheckbox(row);
  };

  constructor(
    private searchService: SearchService,
    private vendorService: VendorService,
    private toasterService: ToasterService,
    private dataService: DataService,
    private exportService: ExportService,
  ) {}

  ngOnInit() {
    this.generateMonthOptions();
    this.vendorService.currentVendor$.subscribe({
      next: (vendor) => {
        if (vendor) {
          this.getTimesheetDetails();
        }
      },
    });
  }

  openCalendar(event: MouseEvent, rowData: any): void {
    this.selectedRowData = rowData;
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.calendarPosition = {
      top: `${rect.bottom + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
    };

    this.isCalendarOpen = true;
    document.body.classList.add('dim-background');
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
    this.selectedRowData = null;
    document.body.classList.remove('dim-background');
  }

  openTimesheetCalendar(rowData: any): void {
    this.selectedRowData = rowData;
    this.isTimesheetCalendarOpen = true;
  }

  closeTimesheetCalendar(): void {
    this.isTimesheetCalendarOpen = false;
    this.selectedRowData = null;
  }

  selectedyear: number = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getFullYear();
  selectedmonth: any = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getMonth() + 1;

  onMonthSelect(event: any): void {
    this.selectedOption = event;
    const t = this.setMonthAndYear(this.selectedOption);
    this.selectedyear = Number(t.split('-')[1]);
    this.selectedmonth= Number(t.split('-')[0]);
    this.getTimesheetDetails();
  }

  generateMonthOptions(): void {
    const today = new Date();
    // Default to the previous month (timesheets are reviewed a month in arrears).
    const defaultDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    this.month = defaultDate.getMonth() + 1;
    this.year = defaultDate.getFullYear();
    this.monthOptions = Array.from({ length: 13 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return {
        label: `${monthName}, ${year}`,
        value: `${date.getMonth() + 1}-${year}`,
        selected: i === 1,
      };
    });
  }

  private setMonthAndYear(selectedOption: string) {
    const [monthValue, yearValue] = selectedOption.split(', ');
    const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
    const formattedMonth = monthStr < 10 ? `0${monthStr}` : `${monthStr}`;
    this.month = parseInt(formattedMonth);
    this.year = parseInt(yearValue);
    return this.month + "-" + this.year;
  }

  getTimesheetDetails(): void {
    this.vendorService.getTimesheetDetails(this.month, this.year).subscribe({
      next: (res: any) => {
        if (res && res.data && res.data.length > 0) {
          this.processTimesheetData(res.data);
        } else {
          this.displayData = [];
          this.data = [];
          this.updateDataLoadedStatus([]);
        }
      },
      error: (error) => {
        this.toasterService.showError(TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_TIMESHEET, TIMESHEET_CONSTANTS.TITLES.ERROR);
        this.displayData = [];
        this.data = [];
        this.isDataLoaded = false;
        this.errorMsg = ErrorMessages.NoDataAvailable;
      },
    });
  }

  private processTimesheetData(rawData: TimeSheetData[]): void {
    this.originalData = rawData.map((item) => {
      const processedItem = { ...item };

      if (isEmptyTimesheet(processedItem)) {
        processedItem.status = TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION;
      }

      processedItem.selectable = shouldShowTimesheetCheckbox(processedItem, 'vendor-rep' as ComponentType);
      processedItem.selected = false;
      return processedItem;
    });

    this.data = [...this.originalData];

    if (this.isStatusFilterApplied && (this.filterCriteria.project || this.filterCriteria.reportingManager || this.filterCriteria.status || this.filterCriteria.dntLeader || this.filterCriteria.employeeStatus)) {
      this.reapplyFilterCriteria();
    }

    this.applyCurrentFilters();
    this.selectedRows = [];
  }

  private applyCurrentFilters(): void {
    let baseData = [...this.originalData];
    if (this.isStatusFilterApplied) {
      baseData = [...this.statusFilteredData];
    }

    if (this.filterValue && this.filterValue.trim()) {
      const searchValue = this.filterValue.toLowerCase();
      const numberSearch = !isNaN(Number(searchValue));

      const searchableFields = ['employeeName', 'yearsOfExperience', 'location', 'workingHrs', 'billableHrs', 'leavesAvailed', 'status'];

      baseData = baseData.filter(item => {
        if (numberSearch) {
          return searchableFields.some(field =>
            (item as any)[field]?.toString() === searchValue
          );
        } else {
          return searchableFields.some(field =>
            (item as any)[field]?.toString().toLowerCase().includes(searchValue)
          );
        }
      });
    }

    this.displayData = sortByStatusPriority(baseData, 'vendor-rep');
    this.updateDataLoadedStatus(this.displayData);
  }

  resetFilter(data: TimeSheetData[]): void {
    this.isStatusFilterApplied = false;
    this.statusFilteredData = [];
    this.filterCriteria = {};
    this.applyCurrentFilters();
  }

  applyFilter(filterData: TimeSheetData[]): void {
    this.isStatusFilterApplied = true;
    this.statusFilteredData = filterData;
    this.opened = false;
    this.applyCurrentFilters();
  }

  clearInput() {
    this.filterValue = '';
    this.applyCurrentFilters();
  }

  showTip(event: MouseEvent, text: string) {
    const el = event.target as HTMLElement;
    if (!text || el.scrollWidth <= el.clientWidth) {
      return; // only show when the text is actually truncated
    }
    const rect = el.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 6 };
  }

  hideTip() {
    this.hoverTip = { ...this.hoverTip, show: false };
  }

  searchTimesheet(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event as KeyboardEvent);
    this.applyCurrentFilters();
  }

  onStatusSelect(filteredData: TimeSheetData[]): void {
    this.isStatusFilterApplied = true;
    this.statusFilteredData = filteredData;
    this.applyCurrentFilters();
  }

  onFilterCriteriaReceived(criteria: {project?: string, reportingManager?: string, status?: string, dntLeader?: string, employeeStatus?: string}): void {
    this.filterCriteria = criteria;
    this.isStatusFilterApplied = true;
  }

  private reapplyFilterCriteria(): void {
    const filters: [keyof TimeSheetData, any, (a: any, b: any) => boolean][] = [
      ['projectName', this.filterCriteria.project, (a, b) => a === b],
      ['reportingManager', this.filterCriteria.reportingManager, (a, b) => a === b],
      ['l3ManagerName', this.filterCriteria.dntLeader, (a, b) => a === b],
      ['employeeStatus', this.filterCriteria.employeeStatus, (a, b) => {
          if (b?.toLowerCase() === 'Inactive') {
            return a?.toLowerCase() === 'Inactive';
          }
          return a === b;
      }],
      [
        'status',
        this.filterCriteria.status,
        (a, b) => (a?.toLowerCase?.() ?? '') === (b?.toLowerCase?.() ?? ''),
      ],
    ];

    this.statusFilteredData = filters.reduce(
      (data, [key, value, comparator]) =>
        value ? data.filter((item) => comparator(item[key], value)) : data,
      this.originalData,
    );
  }

  isBillableHrsGreater(billableHrs: any, workingHrs: any): boolean {
    return Number(billableHrs) > Number(workingHrs);
  }

  handleEvent(event: boolean) {
    this.opened = event;
  }

  shouldShowCheckbox(row: TimeSheetData): boolean {
    return shouldShowTimesheetCheckbox(row, 'vendor-rep' as ComponentType);
  }

  isRowSelected(row: TimeSheetData): boolean {
    return this.selectedRows.some(selectedRow => selectedRow.employeeId === row.employeeId);
  }

  onCheckboxChange(row: TimeSheetData, checked: any): void {
    const isChecked = typeof checked === 'boolean' ? checked : checked?.target?.checked || false;
    row.selected = isChecked;

    if (isChecked) {
      if (!this.isRowSelected(row) && this.shouldShowCheckbox(row)) {
        this.selectedRows.push(row);
      }
    } else {
      this.selectedRows = this.selectedRows.filter(selectedRow =>
        selectedRow.employeeId !== row.employeeId
      );
    }
  }

  onRowSelected(selectedRows: TimeSheetData[]): void {
    this.selectedRows = selectedRows;
  }

  isSubmitDisabled(): boolean {
    if (this.selectedRows.length === 0) return true;
    return this.selectedRows.every(row =>
        row.status === TIMESHEET_CONSTANTS.STATUS.APPROVED ||
      row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL
    );
  }

  get confirmationDialogMessage(): string {
    const submittableCount = this.selectedRows.filter(
      (row) => (row.billableHrs || 0) > 0,
    ).length;
    return TIMESHEET_CONSTANTS.CONFIRMATION_DIALOG.getMessage(submittableCount);
  }

  openSubmitConfirmation(): void {
    const employeeIds = this.selectedRows?.map((item) => item.employeeId);

    if (!employeeIds || employeeIds.length === 0) {
      this.toasterService.showError(
        TIMESHEET_CONSTANTS.MESSAGES.NO_SELECTION_ERROR,
        TIMESHEET_CONSTANTS.MESSAGES.NO_SELECTION_TITLE,
      );
      return;
    }

    const submittableRows = this.selectedRows.filter(
      (row) => (row.billableHrs || 0) > 0,
    );

    if (submittableRows.length === 0) {
      this.toasterService.showError(
        'Timesheets with 0 Billable hours cannot be submitted',
        TIMESHEET_CONSTANTS.TITLES.ERROR,
      );
      return;
    }

    const hasNonSubmittable = submittableRows.some(
      (row) =>
        row.status === TIMESHEET_CONSTANTS.STATUS.APPROVED ||
        row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL,
    );
    if (hasNonSubmittable) {
      this.toasterService.showError(
        TIMESHEET_CONSTANTS.MESSAGES.APPROVED_CANNOT_SUBMIT,
        TIMESHEET_CONSTANTS.TITLES.ERROR,
      );
      return;
    }
    this.showSubmitConfirmation = true;
  }

  onConfirmSubmit(): void {
    this.showSubmitConfirmation = false;
    const employeeIds = this.selectedRows
      .filter((row) => (row.billableHrs || 0) > 0)
      .map((item) => item.employeeId);
    this.validateStatusBeforeSubmit(employeeIds);
  }

  onCancelSubmit(): void {
    this.showSubmitConfirmation = false;
  }

  private validateStatusBeforeSubmit(employeeIds: string[]): void {
    this.vendorService.getTimesheetDetails(this.selectedmonth, this.selectedyear).subscribe({
        next: (res: any) => {
          if (res && res.data && res.data.length > 0) {
            const statusChangeEmployees = this.checkForStatusChanges(res.data, employeeIds);

            if (statusChangeEmployees.length > 0) {
              this.statusChangeDialogData = statusChangeEmployees;
              this.showStatusChangeDialog = true;
            } else {
              this.proceedWithSubmission(employeeIds);
            }
          } else {
            this.toasterService.showError(
              TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_TIMESHEET,
              TIMESHEET_CONSTANTS.TITLES.ERROR
            );
          }
        },
        error: () => {
          this.toasterService.showError(
            TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_TIMESHEET,
            TIMESHEET_CONSTANTS.TITLES.ERROR
          );
        }
      });
  }

  private checkForStatusChanges(freshData: TimeSheetData[], selectedEmployeeIds: string[]): { employeeId: string; employeeName: string }[] {
    const statusChangeEmployees: { employeeId: string; employeeName: string }[] = [];

    selectedEmployeeIds.forEach(employeeId => {
      const freshEmployee = freshData.find(emp => emp.employeeId === employeeId);
      const currentEmployee = this.selectedRows.find(emp => emp.employeeId === employeeId);

      if (freshEmployee && currentEmployee) {
        if (freshEmployee.status === TIMESHEET_CONSTANTS.STATUS.APPROVED &&
            currentEmployee.status !== TIMESHEET_CONSTANTS.STATUS.APPROVED) {
          statusChangeEmployees.push({
            employeeId: freshEmployee.employeeId,
            employeeName: freshEmployee.employeeName
          });
        }
      }
    });

    return statusChangeEmployees;
  }

  private proceedWithSubmission(employeeIds: string[]): void {
    const userProfile = this.dataService?.userProfile();
    const loggedInUserName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ');

    const payload = {
      employeeId: employeeIds,
      reviewedBy: loggedInUserName,
      status: TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL,
      month: this.selectedmonth,
      year: this.selectedyear,
    };

    this.vendorService.updateTimesheetStatusForVendorRep(payload).subscribe({
      next: (response: TimesheetUpdateResponse) => {
        this.displayData.forEach(row => row.selected = false);
        this.selectedRows = [];
        this.toasterService.showSuccess(response?.reason, response?.status);
        this.getTimesheetDetails();
      },
      error: () => {
        this.toasterService.showError(TIMESHEET_CONSTANTS.MESSAGES.SUBMISSION_FAILED, TIMESHEET_CONSTANTS.TITLES.SUBMISSION_ERROR);
      },
    });
  }
  closeStatusChangeDialog(): void {
    this.showStatusChangeDialog = false;
    this.statusChangeDialogData = [];
    this.getTimesheetDetails();
  }

  excludeFromTimesheets(row: TimeSheetData): void {
    this.pendingExcludeRow = row;
    this.showExcludeConfirmation = true;
  }

  onConfirmExclude(): void {
    this.showExcludeConfirmation = false;
    if (!this.pendingExcludeRow) return;
    const row = this.pendingExcludeRow;
    this.pendingExcludeRow = null;
    this.vendorService.excludeEmployeeFromTimesheets(row.employeeId, this.selectedmonth, this.selectedyear).subscribe({
        next: () => {
        this.toasterService.showSuccess('Employee will not be included in timesheets', 'Success');
          this.getTimesheetDetails();
        },
        error: () => {
          this.toasterService.showError('Some error occurred', 'Error');
        },
      });
  }

  onCancelExclude(): void {
    this.showExcludeConfirmation = false;
    this.pendingExcludeRow = null;
  }

  getHoursSuffix(): string {
    return getHoursSuffix();
  }

  getStatusIconClass(status: string): string {
    return getStatusIconClass(status);
  }

  private updateDataLoadedStatus(data: TimeSheetData[]): void {
    const status = getDataLoadedStatus(data, 'vendor-rep' as ComponentType);
    this.isDataLoaded = status.isDataLoaded;
    this.errorMsg = status.errorMsg;
  }

  navigateToContractorProfile(empId: string) {
    this.contractorId = empId;
    this.showContractorProfile = true;
  }

  download(): void {
    const exportRows = this.prepareExportRows(this.selectedRows);
    this.exportService.handleExport(exportRows, columnsForExport, 'vendor-timesheet');
  }

  private prepareExportRows(rows: any[]): any[] {
    return rows.map(row => {
      const isApproved = row.status === TimesheetStatus.Approved;
      const isPendingApproval = row.status === TimesheetStatus.PendingApproval;
      return {
        ...row,
        reviewedBy: isApproved ? row.reviewedBy : '',
        reviewedDate: isApproved ? row.reviewedDate : '',
        submittedBy: isPendingApproval ? row.reviewedBy : '',
        submittedDate: isPendingApproval ? row.reviewedDate : '',
      };
    });
  }
}